import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, AlertTriangle, CheckCircle2, RotateCcw } from "lucide-react";
import { Reveal } from "./Reveal";

const OCCUPANT_OPTIONS = [
  { value: "lt3", label: "Fewer than 3" },
  { value: "3to4", label: "3 or 4" },
  { value: "5plus", label: "5 or more" },
];

const SHARED_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const ARTICLE4_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unknown", label: "Not sure" },
];

const getOutcome = (occupants, shared, article4) => {
  // Not an HMO at all
  if (occupants === "lt3" || shared === "no") {
    return {
      tone: "safe",
      icon: CheckCircle2,
      title: "Likely not classified as an HMO",
      summary:
        "On the information given, this property wouldn't meet the standard definition of a House in Multiple Occupation — so HMO licensing shouldn't apply.",
      actions: [
        "Standard rental regulations still apply (gas, electrical and smoke alarm safety).",
        "If circumstances change — more sharers move in, or facilities are shared — reassess.",
      ],
    };
  }

  // 5+ sharers with shared facilities = mandatory licence
  if (occupants === "5plus" && shared === "yes") {
    return {
      tone: "mandatory",
      icon: AlertTriangle,
      title: "A mandatory HMO licence is almost certainly required",
      summary:
        "Any HMO occupied by five or more people forming more than one household requires a mandatory HMO licence in England, regardless of the number of storeys.",
      actions: [
        "Apply to the local council for a mandatory HMO licence before letting.",
        article4 === "yes"
          ? "The property sits in an Article 4 area — planning permission for HMO use will likely also be needed."
          : article4 === "unknown"
            ? "Check whether the property sits in an Article 4 area — planning permission may also be needed."
            : "Confirm permitted development rights still allow HMO use locally.",
        "Operating an unlicensed HMO can lead to significant financial penalties and rent repayment orders.",
      ],
    };
  }

  // 3-4 sharers with shared facilities = possibly additional licensing
  if (occupants === "3to4" && shared === "yes") {
    return {
      tone: "check",
      icon: ShieldCheck,
      title: "A licence may be required — it depends on the council",
      summary:
        "A property shared by three or four people forming more than one household is a small HMO. Mandatory licensing doesn't apply, but many councils operate additional licensing schemes that cover smaller HMOs too.",
      actions: [
        "Contact the local council to check for additional or selective licensing in that area.",
        article4 === "yes"
          ? "The property is in an Article 4 area — planning permission for change of use to a C4 HMO is likely needed."
          : article4 === "unknown"
            ? "Check whether the area is subject to Article 4 — if yes, planning permission is needed on top of any licence."
            : "Confirm permitted development rights still allow a C4 HMO use locally.",
        "Even without a licence requirement, management and safety standards still apply.",
      ],
    };
  }

  return null;
};

const toneStyles = {
  safe: {
    bar: "bg-forest",
    badge: "text-cream bg-forest",
    icon: "text-gold-soft",
    panel: "bg-cream-3 border-forest/15",
  },
  check: {
    bar: "bg-gold",
    badge: "text-ink bg-gold-soft",
    icon: "text-gold-dark",
    panel: "bg-cream-3 border-gold/40",
  },
  mandatory: {
    bar: "bg-ink",
    badge: "text-cream bg-ink",
    icon: "text-gold-soft",
    panel: "bg-forest text-cream border-gold/40",
  },
};

const StepRadio = ({ label, name, options, value, onChange, testid }) => (
  <fieldset>
    <legend className="text-[11px] tracking-[0.22em] uppercase font-bold text-forest mb-3">
      {label}
    </legend>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`${testid}-${opt.value}`}
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={`min-h-[48px] px-4 py-3 text-[12.5px] font-bold tracking-[0.1em] uppercase border-2 transition-colors duration-300 ${
              active
                ? "bg-forest text-cream border-forest"
                : "bg-white text-forest border-forest hover:bg-cream-2"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </fieldset>
);

export const HmoLicenceChecker = () => {
  const [occupants, setOccupants] = useState(null);
  const [shared, setShared] = useState(null);
  const [article4, setArticle4] = useState(null);

  const complete = occupants && shared && article4;
  const outcome = useMemo(
    () => (complete ? getOutcome(occupants, shared, article4) : null),
    [occupants, shared, article4, complete]
  );

  const reset = () => {
    setOccupants(null);
    setShared(null);
    setArticle4(null);
  };

  const tone = outcome ? toneStyles[outcome.tone] : null;

  return (
    <section data-testid="hmo-checker" className="bg-cream-3 border-y border-forest/10">
      <div className="wrap-x py-20 sm:py-24">
        <Reveal>
          <div className="max-w-2xl">
            <p className="eyebrow text-gold-dark">Quick Check</p>
            <h2 className="font-serif text-4xl sm:text-[44px] font-medium text-forest leading-[1.1] mt-5 mb-5">
              HMO Licensing Checker
            </h2>
            <p className="text-[14.5px] leading-[1.8] text-sage">
              Three quick questions to see whether your property is likely to need an HMO licence.
              This is guidance only — the final answer always sits with your local council.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-start">
          <Reveal>
            <div className="bg-white border border-forest/10 p-7 sm:p-10 space-y-8">
              <StepRadio
                label="01 — How many unrelated people will live there (forming more than one household)?"
                name="occupants"
                options={OCCUPANT_OPTIONS}
                value={occupants}
                onChange={setOccupants}
                testid="hmo-q-occupants"
              />
              <StepRadio
                label="02 — Will they share a kitchen, bathroom or toilet?"
                name="shared"
                options={SHARED_OPTIONS}
                value={shared}
                onChange={setShared}
                testid="hmo-q-shared"
              />
              <StepRadio
                label="03 — Is the property in an Article 4 area (planning restriction on HMOs)?"
                name="article4"
                options={ARTICLE4_OPTIONS}
                value={article4}
                onChange={setArticle4}
                testid="hmo-q-article4"
              />

              {complete && (
                <button
                  type="button"
                  onClick={reset}
                  data-testid="hmo-checker-reset"
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold text-forest/70 hover:text-forest transition-colors"
                >
                  <RotateCcw size={14} strokeWidth={1.8} /> Reset
                </button>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            {!outcome && (
              <div
                data-testid="hmo-checker-placeholder"
                className="bg-forest text-cream p-7 sm:p-10 min-h-[320px] flex items-center grain"
              >
                <p className="text-cream/65 text-sm leading-relaxed max-w-sm relative z-[2]">
                  Answer the three questions and your preliminary HMO licensing position will
                  appear here — along with the next practical steps.
                </p>
              </div>
            )}

            {outcome && (
              <div
                data-testid={`hmo-checker-result-${outcome.tone}`}
                className={`relative border p-7 sm:p-10 ${tone.panel} ${outcome.tone === "mandatory" ? "grain" : ""}`}
              >
                <div className={`absolute left-0 top-0 h-full w-[3px] ${tone.bar}`} />
                <div className="relative z-[2]">
                  <div className="flex items-center gap-3 mb-5">
                    <outcome.icon
                      size={22}
                      strokeWidth={1.6}
                      className={outcome.tone === "mandatory" ? "text-gold-soft" : tone.icon}
                    />
                    <span className={`inline-block text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1.5 ${tone.badge}`}>
                      {outcome.tone === "mandatory"
                        ? "Licence required"
                        : outcome.tone === "check"
                          ? "Check locally"
                          : "Likely not an HMO"}
                    </span>
                  </div>
                  <h3
                    className={`font-serif text-2xl sm:text-[28px] font-medium leading-[1.2] ${
                      outcome.tone === "mandatory" ? "text-cream" : "text-forest"
                    }`}
                  >
                    {outcome.title}
                  </h3>
                  <p
                    className={`text-[13.5px] leading-[1.75] mt-4 ${
                      outcome.tone === "mandatory" ? "text-cream/80" : "text-sage"
                    }`}
                  >
                    {outcome.summary}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {outcome.actions.map((a, i) => (
                      <li
                        key={i}
                        className={`flex gap-3 text-[13px] leading-[1.65] ${
                          outcome.tone === "mandatory" ? "text-cream/80" : "text-ink/75"
                        }`}
                      >
                        <span
                          className={`mt-2 w-1.5 h-1.5 shrink-0 rounded-full ${
                            outcome.tone === "mandatory" ? "bg-gold-soft" : "bg-gold"
                          }`}
                        />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <Link
                      to="/contact"
                      data-testid="hmo-checker-cta"
                      className={outcome.tone === "mandatory" ? "btn-gold" : "btn-forest"}
                    >
                      Discuss This With Us <ArrowRight size={14} strokeWidth={2.2} />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default HmoLicenceChecker;
